import React from 'react';
import { Book, Clock, Calendar, DollarSign, FileText, Settings, ShieldCheck, Zap } from 'lucide-react';

const HelpCenter = () => {
    const sections = [
        {
            title: 'Attendance & Timelog',
            icon: Clock,
            color: '#3b82f6',
            content: [
                'Check In / Check Out: Use the main attendance card to start and end your workday.',
                'Pause & Resume: You can now check out and check in multiple times a day to account for breaks. The system calculates your total working duration automatically.',
                'History: View all your sessions for the day directly on the attendance page.'
            ]
        },
        {
            title: 'Leave Management',
            icon: Calendar,
            color: '#10b981',
            content: [
                'Applying for Leave: Select leave type (Casual, Sick, Earned, Unpaid) and date range.',
                'Approval: Your manager will be notified and can approve or reject the request.',
                'Balance: Track your remaining leave days in real-time.',
                'Export: Use the export button to download your leave history in CSV format.'
            ]
        },
        {
            title: 'Expense Reporting',
            icon: DollarSign,
            color: '#f59e0b',
            content: [
                'Drafting: Create reports and add individual expense items with merchant details.',
                'Receipts: Upload support documents for audit verification.',
                'Approval Flow: Reports go through Manager review and Finance final audit.',
                'Export: Generate PDF summaries of your expense reports.'
            ]
        },
        {
            title: 'Document Tools',
            icon: FileText,
            color: '#6366f1',
            content: [
                'Image to PDF: Convert multiple photos into a single high-quality PDF.',
                'PDF Merge: Combine different PDF documents easily.',
                'OCR: Extract text from images for copy-pasting.'
            ]
        },
        {
            title: 'Admin & Operations',
            icon: Settings,
            color: '#64748b',
            content: [
                'Dynamic Branding: Update Organization name and logo via deployment settings.',
                'User Management: Onboard employees and assign roles (Admin, HR, Manager, Finance).',
                'RBAC: Access to reports and approvals is strictly controlled by your assigned role.'
            ]
        }
    ];

    return (
        <div className="container section">
            <div style={{ marginBottom: '3rem', textAlign: 'center' }}>
                <h1 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#111827', marginBottom: '1rem' }}>
                    Help Center & Documentation
                </h1>
                <p style={{ color: '#6b7280', fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto' }}>
                    Everything you need to know about using the PathtoIP Tools suite efficiently.
                </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                {sections.map((section, idx) => (
                    <div key={idx} className="glass-card" style={{ padding: '2rem', height: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div style={{
                                padding: '0.75rem',
                                background: `${section.color}15`,
                                borderRadius: '0.75rem',
                                color: section.color
                            }}>
                                <section.icon size={24} />
                            </div>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>{section.title}</h2>
                        </div>
                        <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                            {section.content.map((item, i) => (
                                <li key={i} style={{
                                    display: 'flex',
                                    gap: '0.75rem',
                                    marginBottom: '1rem',
                                    color: '#4b5563',
                                    fontSize: '0.95rem',
                                    lineHeight: '1.5'
                                }}>
                                    <Zap size={14} style={{ marginTop: '0.25rem', flexShrink: 0, color: section.color }} />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            <div style={{
                marginTop: '4rem',
                padding: '3rem',
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderRadius: '1.5rem',
                color: 'white',
                textAlign: 'center'
            }}>
                <ShieldCheck size={48} style={{ marginBottom: '1rem', color: '#10b981' }} />
                <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1rem' }}>Enterprise Security & Support</h2>
                <p style={{ color: '#94a3b8', maxWidth: '600px', margin: '0 auto 2rem' }}>
                    This tool suite is designed with role-based access control and persistent storage to ensure your data is always safe.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', fontSize: '0.9rem' }}>
                    <span>Version: <strong>1.1.0</strong></span>
                    <span>Status: <strong>Stable (LTS)</strong></span>
                    <span>License: <strong>Proprietary</strong></span>
                </div>
            </div>
        </div>
    );
};

export default HelpCenter;
